import * as THREE from 'three';
import type { CameraMode } from './types';
import type { Fencer } from './Fencer';

const LABELS: Record<CameraMode, string> = {
  side: 'SIDE VIEW',
  third: '3RD PERSON',
  fps: 'FPS · MASK',
};

export class CameraSystem {
  mode: CameraMode = 'side';
  readonly camera: THREE.PerspectiveCamera;

  private target = new THREE.Vector3();
  private pos = new THREE.Vector3();
  private look = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private shake = 0;
  private transition = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.05, 120);
    this.camera.position.set(0, 2.2, 7.5);
  }

  setMode(mode: CameraMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.transition = 1;
  }

  cycle(): CameraMode {
    const order: CameraMode[] = ['side', 'third', 'fps'];
    const i = order.indexOf(this.mode);
    this.setMode(order[(i + 1) % order.length]);
    return this.mode;
  }

  label(): string {
    return LABELS[this.mode];
  }

  addShake(amount: number): void {
    this.shake = Math.min(0.35, this.shake + amount);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, player: Fencer, opponent: Fencer): void {
    this.transition = Math.max(0, this.transition - dt * 1.8);
    this.shake = Math.max(0, this.shake - dt * 1.5);

    const midX = (player.state.x + opponent.state.x) * 0.5;
    const sep = Math.abs(player.state.x - opponent.state.x);

    let desiredPos = this.pos;
    let desiredLook = this.look;

    if (this.mode === 'side') {
      // Mortal Kombat / fighting-game strip camera
      const zoom = THREE.MathUtils.clamp(4.5 + sep * 0.55, 5.5, 10);
      desiredPos = this.tmp.set(midX, 1.55, zoom);
      desiredLook.set(midX, 1.15, 0);
      this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, 48, 0.08);
    } else if (this.mode === 'third') {
      const behind = player.state.facing * -2.4;
      desiredPos = this.tmp.set(player.state.x + behind, 1.85, 2.1);
      opponent.getChestWorld(desiredLook);
      desiredLook.y = 1.35;
      this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, 55, 0.08);
    } else {
      // FPS from inside mask
      player.getEyeWorld(desiredPos);
      desiredPos.y += 0.02;
      // Look along blade toward opponent
      opponent.getChestWorld(desiredLook);
      desiredLook.y = 1.4;
      // Slight offset toward opponent tip tracking
      this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, 68, 0.1);
    }

    const lerp = this.mode === 'fps' ? 1 - Math.pow(0.001, dt) : 1 - Math.pow(0.02, dt);
    this.pos.lerp(desiredPos, this.mode === 'fps' ? Math.min(1, lerp * 1.5) : lerp);
    this.look.lerp(desiredLook, lerp);

    // Shake
    const sx = (Math.random() - 0.5) * this.shake;
    const sy = (Math.random() - 0.5) * this.shake;

    this.camera.position.copy(this.pos);
    this.camera.position.x += sx;
    this.camera.position.y += sy;
    this.camera.lookAt(this.look);
    this.camera.updateProjectionMatrix();
  }
}
